import { FunctionComponent, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Modal, Icon } from "@/components";
import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import {
	useGetZohoFieldMappingsQuery,
	useGetZohoLeadFieldsQuery,
	useGetZohoContactFieldsQuery,
	useGetZohoAccountFieldsQuery,
	useGetZohoDealFieldsQuery,
	useGetZohoSourceFieldsQuery,
	useCreateZohoFieldMappingMutation,
	useUpdateZohoFieldMappingMutation,
	useDeleteZohoFieldMappingMutation,
	useTestZohoAiFieldMappingMutation,
} from "@/pages/Private/redux/zoho/zoho.api";
import { useLazyGetContactsQuery } from "@/pages/Private/redux/contact/contact.api";

import {
	ZohoEntityType,
	ZohoField,
	ZohoSourceField,
	FieldMappingSourceType,
	ZohoFieldMapping,
	FieldMappingAiConfig,
} from "../schema/zoho";
import { ZohoMappingRow, ZohoNewMappingRow, NewZohoMapping } from "./ZohoMappingRow";
import { ZohoMappingPreviewModal } from "./ZohoMappingPreviewModal";
import {
	AiConfigModal,
	buildAiConfigLabels,
	buildAiProspectOptions,
	FieldMappingFooter,
} from "./fieldMapping";

enum Tabs {
	LEADS = "LEADS",
	CONTACTS = "CONTACTS",
	ACCOUNTS = "ACCOUNTS",
	DEALS = "DEALS",
}

interface ZohoFieldMappingModalProps {
	isOpen: boolean;
	onClose: () => void;
}

interface ZohoAiConfigSelection {
	id?: number;
	tempId?: string;
	label: string;
	targetField: ZohoField;
	allFields: ZohoField[];
	entityType: ZohoEntityType;
	aiConfig?: FieldMappingAiConfig;
}

const TAB_ENTITY_MAP: Record<Tabs, ZohoEntityType> = {
	[Tabs.LEADS]: ZohoEntityType.LEAD,
	[Tabs.CONTACTS]: ZohoEntityType.CONTACT,
	[Tabs.ACCOUNTS]: ZohoEntityType.ACCOUNT,
	[Tabs.DEALS]: ZohoEntityType.DEAL,
};

export const ZohoFieldMappingModal: FunctionComponent<ZohoFieldMappingModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>): string =>
		t(`company.zoho.fieldMapping.${key}`, options as never) as unknown as string;

	const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.LEADS);
	const [newMappings, setNewMappings] = useState<NewZohoMapping[]>([]);
	const [savingTempId, setSavingTempId] = useState<string | null>(null);
	const [aiConfigTarget, setAiConfigTarget] = useState<ZohoAiConfigSelection | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const aiProspectSearchRequestRef = useRef<{ abort?: () => void } | null>(null);

	const entityType = TAB_ENTITY_MAP[currentTab];

	// Fetch mappings per entity type
	const { data: leadMappings, isLoading: isLoadingLeads } = useGetZohoFieldMappingsQuery({
		entityType: ZohoEntityType.LEAD,
	});
	const { data: contactMappings, isLoading: isLoadingContacts } = useGetZohoFieldMappingsQuery({
		entityType: ZohoEntityType.CONTACT,
	});
	const { data: accountMappings, isLoading: isLoadingAccounts } = useGetZohoFieldMappingsQuery({
		entityType: ZohoEntityType.ACCOUNT,
	});
	const { data: dealMappings, isLoading: isLoadingDeals } = useGetZohoFieldMappingsQuery({
		entityType: ZohoEntityType.DEAL,
	});

	// Fetch Zoho CRM fields
	const { data: leadFields } = useGetZohoLeadFieldsQuery();
	const { data: contactFields } = useGetZohoContactFieldsQuery();
	const { data: accountFields } = useGetZohoAccountFieldsQuery();
	const { data: dealFields } = useGetZohoDealFieldsQuery();
	const { data: sourceFields } = useGetZohoSourceFieldsQuery();

	// Mutations
	const [createMapping] = useCreateZohoFieldMappingMutation();
	const [updateMapping] = useUpdateZohoFieldMappingMutation();
	const [deleteMapping] = useDeleteZohoFieldMappingMutation();
	const [testAiMapping] = useTestZohoAiFieldMappingMutation();
	const [searchProspects] = useLazyGetContactsQuery();

	const isLoading = isLoadingLeads || isLoadingContacts || isLoadingAccounts || isLoadingDeals;

	// Get fields/mappings for current tab
	const getMappingsForTab = (tab: Tabs) => {
		switch (tab) {
			case Tabs.LEADS:
				return leadMappings;
			case Tabs.CONTACTS:
				return contactMappings;
			case Tabs.ACCOUNTS:
				return accountMappings;
			case Tabs.DEALS:
				return dealMappings;
		}
	};

	const getZohoFieldsForTab = (tab: Tabs): ZohoField[] => {
		switch (tab) {
			case Tabs.LEADS:
				return leadFields?.fields || [];
			case Tabs.CONTACTS:
				return contactFields?.fields || [];
			case Tabs.ACCOUNTS:
				return accountFields?.fields || [];
			case Tabs.DEALS:
				return dealFields?.fields || [];
		}
	};

	const getSourceFieldsForTab = (tab: Tabs): ZohoSourceField[] => {
		if (tab === Tabs.LEADS) {
			return sourceFields?.leadFields || sourceFields?.contactFields || [];
		}

		if (tab === Tabs.ACCOUNTS) {
			return sourceFields?.accountFields || [];
		}

		if (tab === Tabs.DEALS) {
			return sourceFields?.dealFields || sourceFields?.contactFields || [];
		}

		return sourceFields?.contactFields || [];
	};

	// Source-field uniqueness only matters in FIELD mode (constants don't consume the picker namespace).
	const isFieldMode = (sourceType?: FieldMappingSourceType) =>
		(sourceType ?? FieldMappingSourceType.FIELD) === FieldMappingSourceType.FIELD;

	const usedSourceFields = useMemo(() => {
		const mappings: ZohoFieldMapping[] | undefined = getMappingsForTab(currentTab);
		const saved =
			mappings
				?.filter((m) => isFieldMode(m.sourceType) && m.sourceField)
				.map((m) => m.sourceField) || [];
		const unsaved = newMappings
			.filter(
				(m) =>
					m.entityType === TAB_ENTITY_MAP[currentTab] && isFieldMode(m.sourceType) && m.sourceField
			)
			.map((m) => m.sourceField);

		return [...saved, ...unsaved];
	}, [currentTab, leadMappings, contactMappings, accountMappings, dealMappings, newMappings]);

	const usedZohoFieldsByType = useMemo<Record<FieldMappingSourceType, string[]>>(() => {
		const mappings: ZohoFieldMapping[] | undefined = getMappingsForTab(currentTab);
		const byType: Record<FieldMappingSourceType, string[]> = {
			[FieldMappingSourceType.FIELD]: [],
			[FieldMappingSourceType.CONSTANT]: [],
			[FieldMappingSourceType.AI]: [],
		};

		mappings
			?.filter((m) => m.zohoField)
			.forEach((m) => byType[m.sourceType ?? FieldMappingSourceType.FIELD].push(m.zohoField));
		newMappings
			.filter((m) => m.entityType === TAB_ENTITY_MAP[currentTab] && m.zohoField)
			.forEach((m) => byType[m.sourceType].push(m.zohoField));

		return byType;
	}, [currentTab, leadMappings, contactMappings, accountMappings, dealMappings, newMappings]);

	const handleConfigureAi = (
		mapping: {
			id?: number;
			tempId?: string;
			zohoField: string;
			entityType: ZohoEntityType | string;
			aiConfig?: FieldMappingAiConfig;
		},
		fields: ZohoField[]
	) => {
		const target = fields.find((f) => f.name === mapping.zohoField) ?? {
			name: mapping.zohoField,
			label: mapping.zohoField,
			type: "string",
		};

		setAiConfigTarget({
			id: mapping.id,
			tempId: mapping.tempId,
			label: target.label,
			targetField: target,
			allFields: fields,
			entityType: mapping.entityType as ZohoEntityType,
			aiConfig: mapping.aiConfig,
		});
	};

	const handleSearchAiProspects = async (query: string) => {
		aiProspectSearchRequestRef.current?.abort?.();
		const request = searchProspects({ page: 1, limit: 10, searchValue: query });

		aiProspectSearchRequestRef.current = request;

		try {
			const result = await request.unwrap();

			return buildAiProspectOptions(result.data);
		} catch {
			return [];
		} finally {
			if (aiProspectSearchRequestRef.current === request) {
				aiProspectSearchRequestRef.current = null;
			}
		}
	};

	const handleCancelAiProspectSearch = () => {
		aiProspectSearchRequestRef.current?.abort?.();
		aiProspectSearchRequestRef.current = null;
	};

	const handleSaveAiConfig = async (config: FieldMappingAiConfig) => {
		if (!aiConfigTarget) {
			return;
		}

		if (aiConfigTarget.tempId) {
			handleUpdateNewMapping(aiConfigTarget.tempId, {
				aiConfig: config,
				sourceField: aiConfigTarget.label,
			});
		} else if (aiConfigTarget.id) {
			await updateMapping({
				id: aiConfigTarget.id,
				data: {
					sourceType: FieldMappingSourceType.AI,
					sourceField: aiConfigTarget.label,
					aiConfig: config,
				},
			}).unwrap();
		}

		setAiConfigTarget(null);
	};

	const handleAddMapping = () => {
		const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

		setNewMappings((prev) => [
			...prev,
			{
				tempId,
				sourceField: "",
				sourceType: FieldMappingSourceType.FIELD,
				zohoField: "",
				isActive: true,
				entityType,
			},
		]);
	};

	const handleUpdateNewMapping = (tempId: string, data: Partial<NewZohoMapping>) => {
		setNewMappings((prev) => prev.map((m) => (m.tempId === tempId ? { ...m, ...data } : m)));
	};

	const handleSaveNewMapping = async (mapping: NewZohoMapping) => {
		if (
			!mapping.zohoField ||
			(mapping.sourceType !== FieldMappingSourceType.AI && !mapping.sourceField) ||
			(mapping.sourceType === FieldMappingSourceType.AI && !mapping.aiConfig?.prompt)
		) {
			return;
		}

		setSavingTempId(mapping.tempId);

		try {
			await createMapping({
				sourceField: mapping.sourceField || mapping.zohoField,
				sourceType: mapping.sourceType,
				zohoField: mapping.zohoField,
				entityType: mapping.entityType as ZohoEntityType,
				isActive: mapping.isActive,
				aiConfig: mapping.aiConfig,
			}).unwrap();
			setNewMappings((prev) => prev.filter((m) => m.tempId !== mapping.tempId));
		} catch (error) {
			console.error("Failed to save mapping:", error);
		} finally {
			setSavingTempId(null);
		}
	};

	const handleCancelNewMapping = (tempId: string) => {
		setNewMappings((prev) => prev.filter((m) => m.tempId !== tempId));
	};

	const handleToggleActive = async (id: number, isActive: boolean) => {
		await updateMapping({ id, data: { isActive } });
	};

	const handleDeleteMapping = async (id: number) => {
		await deleteMapping(id);
	};

	const tabItems = [
		{ key: Tabs.LEADS, label: ts("leads") },
		{ key: Tabs.CONTACTS, label: ts("contacts") },
		{ key: Tabs.ACCOUNTS, label: ts("accounts") },
		{ key: Tabs.DEALS, label: ts("deals") },
	];

	const renderTabContent = (tab: Tabs) => {
		const mappings = getMappingsForTab(tab);
		const zohoFields = getZohoFieldsForTab(tab);
		const srcFields = getSourceFieldsForTab(tab);
		const tabNewMappings = newMappings.filter((m) => m.entityType === TAB_ENTITY_MAP[tab]);

		return (
			<div className="mt-2">
				<div className="flex items-center gap-3 py-3 border-b border-gray-200 font-medium text-sm text-gray-600">
					<div className="flex-1">{ts("ourField")}</div>
					<div className="w-[24px]"></div>
					<div className="flex-1">{ts("zohoField")}</div>
					<div className="w-[60px] text-center">{ts("active")}</div>
					<div className="w-[80px]"></div>
				</div>

				{isLoading ? (
					<LoadingBlock className="py-8" />
				) : (
					<>
						{mappings?.map((mapping) => (
							<ZohoMappingRow
								key={mapping.id}
								mapping={mapping}
								sourceFields={srcFields}
								ts={ts}
								zohoFields={zohoFields}
								onConfigureAi={(m) => handleConfigureAi(m, zohoFields)}
								onDelete={handleDeleteMapping}
								onToggleActive={handleToggleActive}
							/>
						))}
						{tabNewMappings.map((mapping) => (
							<ZohoNewMappingRow
								key={mapping.tempId}
								isSaving={savingTempId === mapping.tempId}
								mapping={mapping}
								sourceFields={srcFields}
								ts={ts}
								usedSourceFields={usedSourceFields}
								usedZohoFieldsByType={usedZohoFieldsByType}
								zohoFields={zohoFields}
								onCancel={handleCancelNewMapping}
								onConfigureAi={(m) => handleConfigureAi(m, zohoFields)}
								onSave={handleSaveNewMapping}
								onUpdate={handleUpdateNewMapping}
							/>
						))}
						{!mappings?.length && !tabNewMappings.length && (
							<div className="py-6 text-center text-gray-500">{ts("noMappings")}</div>
						)}
					</>
				)}

				<div className="mt-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-[16px] h-[16px] text-gray-700 mr-2" icon={faPlus} />}
						size={ButtonSize.S}
						title={ts("addMapping")}
						onClick={handleAddMapping}
					/>
				</div>

				{(() => {
					const aiCount =
						(mappings?.filter((m) => m.sourceType === FieldMappingSourceType.AI).length ?? 0) +
						tabNewMappings.filter((m) => m.sourceType === FieldMappingSourceType.AI).length;
					const mappedCount = (mappings?.length ?? 0) + tabNewMappings.length;

					return (
						<FieldMappingFooter
							aiCount={aiCount}
							creditPillLabel={ts("footerCreditPill")}
							mappedCount={mappedCount}
							previewLabel={ts("preview.button")}
							summaryLabel={ts("footerSummary", { mapped: mappedCount, ai: aiCount })}
							testIdPrefix="zoho"
							onPreview={() => setIsPreviewOpen(true)}
						/>
					);
				})()}
			</div>
		);
	};

	return (
		<Modal handleClose={onClose} isOpened={isOpen} size="lg" title={ts("title")}>
			<div className="p-4">
				<div className="flex border-b border-gray-200 mb-2">
					{tabItems.map((tab) => (
						<button
							key={tab.key}
							className={classNames(
								"px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer",
								currentTab === tab.key
									? "border-brand-light text-brand-light"
									: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
							)}
							type="button"
							onClick={() => setCurrentTab(tab.key)}
						>
							{tab.label}
						</button>
					))}
				</div>
				{renderTabContent(currentTab)}
			</div>
			<AiConfigModal
				allFields={aiConfigTarget?.allFields ?? []}
				callbacks={{
					onCancelSearchProspects: handleCancelAiProspectSearch,
					onSearchProspects: handleSearchAiProspects,
					onRunTest: (prospectId, prompt, fallbackValue) =>
						testAiMapping({
							prospectId,
							entityType: aiConfigTarget?.entityType ?? entityType,
							zohoField: aiConfigTarget?.targetField.name,
							prompt,
							allowedValues: aiConfigTarget?.targetField.options?.map((option) => option.value),
							fallbackValue,
						}).unwrap(),
				}}
				initialConfig={aiConfigTarget?.aiConfig}
				isOpen={Boolean(aiConfigTarget)}
				labels={buildAiConfigLabels(ts)}
				subtitle={ts("aiConfigSubtitle", { label: aiConfigTarget?.label ?? "" })}
				targetField={aiConfigTarget?.targetField}
				title={ts("aiConfigTitle")}
				onClose={() => setAiConfigTarget(null)}
				onSave={handleSaveAiConfig}
			/>
			<ZohoMappingPreviewModal
				entityType={entityType}
				isOpen={isPreviewOpen}
				sourceFields={getSourceFieldsForTab(currentTab)}
				ts={ts}
				zohoFields={getZohoFieldsForTab(currentTab)}
				onCancelSearchProspects={handleCancelAiProspectSearch}
				onClose={() => setIsPreviewOpen(false)}
				onSearchProspects={handleSearchAiProspects}
			/>
		</Modal>
	);
};
